/**
 * TaskOverviewSection Component
 *
 * Single card combining KPI strip + progress chart.
 * KPIs in a 4-column strip, chart below with legend in header.
 *
 * @module pages/DashboardPage/components/TaskOverviewSection
 */

import { memo } from 'react';
import type { CSSProperties } from 'react';
import { Skeleton } from '@mantine/core';
import { useTranslation } from 'react-i18next';
import { IconClipboardList } from '@tabler/icons-react';

import { useDashboardTaskProgress } from '@/hooks';
import { StatCardSkeleton } from '@/components/common';

import { KpiCard } from '../KpiCard';
import { TaskProgressChart } from '../TaskProgressChart';
import {
  CHART_SERIES,
  EMPTY_STATS,
  KPI_CONFIG,
  KPI_ICON_SIZE,
} from './TaskOverviewSection.utils';
import type { TaskOverviewSectionProps } from './TaskOverviewSection.types';
import styles from './TaskOverviewSection.module.css';

/** Icon size for section header icons (px) */
const HEADER_ICON_SIZE = 16;

/** Chart height for compact layout */
const CHART_HEIGHT = 180;

export const TaskOverviewSection = memo(function TaskOverviewSection({
  dateRange,
  taskStats,
  isLoading,
}: TaskOverviewSectionProps) {
  const { t } = useTranslation();

  const { taskProgress: chartData } = useDashboardTaskProgress(dateRange);

  const stats = taskStats ?? EMPTY_STATS;
  const chartPoints = chartData ?? [];
  const showSkeleton = isLoading && !taskStats;

  return (
    <section className={styles.section}>
      <div className={styles.cardHeader}>
        <div className={styles.headerTitle}>
          <IconClipboardList size={HEADER_ICON_SIZE} />
          <span>{t('dashboard.taskSummary.title')}</span>
          <span className={styles.headerDot}>&middot;</span>
          {showSkeleton ? (
            <Skeleton height={14} width={50} radius="sm" />
          ) : (
            <span className={styles.headerCount}>
              {stats.total} {t('dashboard.taskSummary.totalSuffix')}
            </span>
          )}
        </div>
        <div className={styles.legend}>
          {CHART_SERIES.map((series) => (
            <div key={series.dataKey} className={styles.legendItem}>
              <span
                className={styles.legendDot}
                style={{ '--legend-dot-color': series.color } as CSSProperties}
              />
              <span className={styles.legendLabel}>{t(series.labelKey)}</span>
            </div>
          ))}
        </div>
      </div>
      <div className={styles.kpiStrip}>
        {showSkeleton
          ? KPI_CONFIG.map((config) => (
              <StatCardSkeleton key={config.key} compact />
            ))
          : KPI_CONFIG.map((config) => (
              <KpiCard
                key={config.key}
                label={t(config.labelKey)}
                value={stats[config.key]}
                total={stats.total}
                icon={<config.Icon size={KPI_ICON_SIZE} />}
                color={config.color}
                compact
              />
            ))}
      </div>
      <div className={styles.chartArea}>
        {showSkeleton ? (
          <Skeleton height={CHART_HEIGHT} radius="md" />
        ) : (
          <TaskProgressChart data={chartPoints} height={CHART_HEIGHT} />
        )}
      </div>
    </section>
  );
});

TaskOverviewSection.displayName = 'TaskOverviewSection';
